import type { ChannelId, HeroBlockInput, HeroContent, OutcomeType } from './hero.types';

export const HERO_LOGO = '/icons/vaanexa-icon.svg';

/** Per-channel presentation. Colour is never the only signal — each has a label. */
export const CHANNEL_META: Record<
  ChannelId,
  { label: string; short: string; href: string; colorVar: string }
> = {
  whatsapp: {
    label: 'WhatsApp',
    short: 'WA',
    href: '/channels/whatsapp',
    colorVar: 'var(--hero-wa)',
  },
  instagram: {
    label: 'Instagram',
    short: 'IG',
    href: '/channels/instagram',
    colorVar: 'var(--hero-ig)',
  },
  messenger: {
    label: 'Messenger',
    short: 'MS',
    href: '/channels/messenger',
    colorVar: 'var(--hero-ms)',
  },
};

export const OUTCOME_ACCENT: Record<OutcomeType, string> = {
  lead: 'var(--hero-glow-accent)',
  appointment: 'var(--hero-glow-secondary)',
  followup: 'var(--hero-wa)',
  payment: '#34d399',
};

/**
 * Safe defaults for everything the CMS block does not yet carry. Illustrative
 * outcome values only — not claims about Vaanexa's performance.
 */
export const DEFAULT_HERO: HeroContent = {
  eyebrow: 'AI-Powered Conversational Commerce',
  headline: 'Turn every conversation into a customer.',
  highlightedText: 'conversation into a customer',
  description:
    'Vaanexa helps businesses manage WhatsApp, Instagram and Messenger conversations, qualify leads, automate follow-ups and move customers from their first message to final conversion.',
  primaryCta: { label: 'Start Free', href: '/signup' },
  secondaryCta: { label: 'Book a Demo', href: '/demo' },
  trustText: 'Built with official Meta APIs for business messaging.',
  enabledChannels: ['whatsapp', 'instagram', 'messenger'],
  outcomeCards: [
    { label: 'Hot Lead', value: 'Score 86', type: 'lead' },
    { label: 'Appointment', value: 'Today, 4:30 PM', type: 'appointment' },
    { label: 'Payment', value: '₹12,500 Received', type: 'payment' },
  ],
  visualVariant: 'conversation-engine',
  motionIntensity: 'standard',
  logoUrl: HERO_LOGO,
};

/** Screen-reader description of the (decorative) conversion visual. */
export const HERO_VISUAL_DESCRIPTION =
  'Vaanexa receives messages from WhatsApp, Instagram and Messenger and transforms them into qualified leads, appointments, follow-ups and payments.';

const VALID_CHANNELS: ChannelId[] = ['whatsapp', 'instagram', 'messenger'];

/**
 * Merge whatever the CMS block provides over the safe defaults. Only known,
 * non-empty fields are taken from the block — no arbitrary markup, no code.
 */
export function deriveHeroContent(block: HeroBlockInput): HeroContent {
  if (!block) return DEFAULT_HERO;
  const str = (v: unknown, fallback: string): string =>
    typeof v === 'string' && v.trim() ? v : fallback;

  return {
    ...DEFAULT_HERO,
    eyebrow: str(block.eyebrow, DEFAULT_HERO.eyebrow),
    headline: str(block.headline, DEFAULT_HERO.headline),
    description: str(block.subheadline, DEFAULT_HERO.description),
    primaryCta: block.primaryCta?.label
      ? {
          label: block.primaryCta.label,
          href: block.primaryCta.href || DEFAULT_HERO.primaryCta.href,
        }
      : DEFAULT_HERO.primaryCta,
    secondaryCta: block.secondaryCta?.label
      ? {
          label: block.secondaryCta.label,
          href: block.secondaryCta.href || DEFAULT_HERO.secondaryCta.href,
        }
      : DEFAULT_HERO.secondaryCta,
    logoUrl: str(block.imageUrl, HERO_LOGO).endsWith('.svg')
      ? str(block.imageUrl, HERO_LOGO)
      : HERO_LOGO,
    enabledChannels: VALID_CHANNELS,
  };
}
